import * as semver from "semver";
import { exec } from "shelljs";

import { ILogger } from "../Commons/Logging/ILogger";
import { getLogger } from "../Commons/Logging/StaticLogger";
import { defaultRegistry } from "../Commons/Defaults";

import { EngineSource } from "./Engine/EngineSource";
import { GeneratorSource } from "./Generator/AcquireFormGenerator";
import { CandyVersionSpec, candyVersionSpecToString } from "./Version";

const candyGeneratorPackageName = "@kontur.candy/generator";
const candyEnginePackageName = "@kontur.candy/engine";

function isStableVersion(version: string): boolean {
    const parsedVersion = semver.parse(version);
    if (parsedVersion == undefined) {
        return false;
    }
    const { prerelease } = parsedVersion;
    return prerelease.length === 0;
}

function isPrereleaseVersionWithPreId(version: string, preid: string): boolean {
    const parsedVersion = semver.parse(version);
    if (parsedVersion == undefined) {
        return false;
    }
    const { prerelease } = parsedVersion;
    return prerelease.length > 0 && prerelease[0] === preid;
}

export async function isEngineVersionExists(version: string): Promise<boolean> {
    return (await getPackageVersionsInReverseOrder(candyEnginePackageName)).includes(version);
}

export async function getLastStableEngineVersion(): Promise<string> {
    const sortedVersions = await getPackageVersionsInReverseOrder(candyEnginePackageName);
    for (const version of sortedVersions) {
        if (isStableVersion(version)) {
            return version;
        }
    }
    throw new Error("Cannot find last stable engine version");
}

export async function getLastStableGeneratorVersion(): Promise<string> {
    const sortedVersions = await getPackageVersionsInReverseOrder(candyGeneratorPackageName);
    for (const version of sortedVersions) {
        if (isStableVersion(version)) {
            return version;
        }
    }
    throw new Error("Cannot find last stable generator version");
}

export async function getLastEnginePrereleaseVersion(preid: string): Promise<string> {
    const sortedVersions = await getPackageVersionsInReverseOrder(candyEnginePackageName);
    for (const version of sortedVersions) {
        if (isPrereleaseVersionWithPreId(version, preid)) {
            return version;
        }
    }
    throw new Error(`Cannot find prerelease for pre:${preid} engine version`);
}

export async function getLastGeneratorPrereleaseVersion(preid: string): Promise<string> {
    const sortedVersions = await getPackageVersionsInReverseOrder(candyGeneratorPackageName);
    for (const version of sortedVersions) {
        if (isPrereleaseVersionWithPreId(version, preid)) {
            return version;
        }
    }
    throw new Error(`Cannot find prerelease for pre:${preid} generator version`);
}

export async function resolveGeneratorVersion(
    versionSpec: CandyVersionSpec,
    logger: ILogger = getLogger()
): Promise<GeneratorSource> {
    const version = await getSuitableVersion(versionSpec, candyGeneratorPackageName);
    logger.withPrefix("GENERATOR").log(`Resolved version ${candyVersionSpecToString(versionSpec)} to ${version}`);
    return {
        type: "PublishedGenerator",
        version: version,
    };
}

export function resolveLocalGeneratorSource(engineDirectory: string, logger: ILogger = getLogger()): GeneratorSource {
    logger.withPrefix("GENERATOR").log(`Resolved version 'local'`);
    return {
        type: "GeneratorSources",
        path: engineDirectory,
    };
}

export function resolveLocalEngineSource(engineDirectory: string, logger: ILogger = getLogger()): EngineSource {
    logger.withPrefix("ENGINE").log(`Resolved version 'local'`);
    return {
        type: "LocalEngineSources",
        path: engineDirectory,
    };
}

export async function resolveEngineVersion(
    versionSpec: CandyVersionSpec,
    logger: ILogger = getLogger()
): Promise<EngineSource> {
    const version = await getSuitableVersion(versionSpec, candyEnginePackageName);
    logger.withPrefix("ENGINE").log(`Resolved version ${candyVersionSpecToString(versionSpec)} to ${version}`);
    return {
        type: "PublishedEngine",
        version: version,
    };
}

export async function getSuitableVersion(versionSpec: CandyVersionSpec, packageName: string): Promise<string> {
    const sortedVersions = await getPackageVersionsInReverseOrder(packageName);
    if (versionSpec.type === "pre") {
        const expectedPrerelease = versionSpec.preId;
        for (const version of sortedVersions) {
            const parsedVersion = semver.parse(version);
            if (parsedVersion == undefined) {
                continue;
            }
            const { prerelease } = parsedVersion;
            if (prerelease.length > 0) {
                if (prerelease[0] === expectedPrerelease) {
                    return version;
                }
            }
        }
        throw new Error(`Package ${packageName} does not contains version satisfies pre:${versionSpec.preId}`);
    }
    const result = sortedVersions.find(x => semver.satisfies(x, versionSpec.version));
    if (result == undefined) {
        throw new Error(`Package ${packageName} does not contains version satisfies ${versionSpec.version}`);
    }
    return result;
}

export async function getPrereleaseVersionFormJson(usePrereleaseVersion: string): Promise<string | undefined> {
    const prereleaseVersion = await getLastEnginePrereleaseVersion(usePrereleaseVersion);

    return prereleaseVersion != undefined ? `pre:${usePrereleaseVersion}` : undefined;
}

const packageVersionsCache: Map<string, string[]> = new Map();

async function getPackageVersionsInReverseOrder(packageName: string): Promise<string[]> {
    const cachedResult = packageVersionsCache.get(packageName);
    if (cachedResult != undefined) {
        return cachedResult;
    }
    const output = await new Promise<string>((resolve, reject) => {
        const command =
            (process.platform === "linux" ? "unbuffer " : "") + `npm view ${packageName} versions --no-progress --json`;
        const { npm_config_registry: _, ...env } = process.env;
        exec(
            command,
            {
                async: true,
                silent: true,
                env: {
                    ...env,
                    NPM_CONFIG_REGISTRY: defaultRegistry,
                },
            },
            (code, stdout, stderr) => {
                if (code !== 0) {
                    reject(new Error(stderr));
                } else {
                    resolve(stdout);
                }
            }
        );
    });
    const versions: string[] = JSON.parse(output.trim());
    const sortedVersions = versions.sort(semver.compare).reverse();
    packageVersionsCache.set(packageName, sortedVersions);
    return sortedVersions;
}
